import { IntelliWidget } from 'intelliprove-widget-sdk';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntelliProveWidgets } from '~providers/widgets';
import { cn } from '~utils/classnames';

export type MetricWidgetOption =
  | 'mental_stress'
  | 'general_fitness'
  | 'mental_health_risk'
  | 'sleep_quality'
  | 'energy_balance'
  | 'hypertension_risk';
export type BiomarkerWidgetOption =
  | 'heart_rate'
  | 'heart_rate_variability'
  | 'respiratory_rate'
  | 'resonant_breathing'
  | 'systolic_blood_pressure'
  | 'diastolic_blood_pressure';

export interface iWidgetConfig {
  parameter?: MetricWidgetOption | BiomarkerWidgetOption;
}

export interface iWidget {
  kind: 'biomarker' | 'metric' | 'recommendations' | 'main' | 'todos';
  config: iWidgetConfig;
  elementId: string;
  variation?: 'default' | 'small';
  themeOverrides?: object;
  onLoaded?: () => void;
  onError?: (e: unknown) => void;
  className?: string;
}

export default function Widget({
  kind,
  config,
  elementId,
  variation,
  themeOverrides = {},
  onLoaded = undefined,
  onError = undefined,
  className = undefined,
}: iWidget) {
  const sdk = useIntelliProveWidgets();
  const widget = useRef<IntelliWidget | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [placeholder, setPlaceholder] = useState<string>('');
  const [isInView, setIsInView] = useState<boolean>(false);
  const [hasLoadedWidget, setHasLoadedWidget] = useState<boolean>(false);

  const selector = useMemo<string>(() => `#${elementId}`, [elementId]);

  // Load loading widget immediately
  useEffect(() => {
    sdk.getLoadingWidget(kind).then((w) => {
      setPlaceholder(w);
    });
  }, [sdk, kind, elementId]);

  // Intersection Observer to detect when component is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const inView = entry.isIntersecting;
        setIsInView(inView);
      },
      {
        threshold: 0.1, // Trigger when 10% of the component is visible
        rootMargin: '50px', // Start loading 50px before the component enters viewport
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [elementId]);

  // Load actual widget when in view
  useEffect(() => {
    if (isInView && !hasLoadedWidget && widget.current === null) {
      setHasLoadedWidget(true);

      sdk
        .getWidget(kind, config, variation, themeOverrides)
        .then((w) => {
          widget.current = w;
          w.mount(selector);
          if (onLoaded) onLoaded();
        })
        .catch((e) => {
          console.error(`[Widget ${elementId}] Widget loading failed:`, e);
          sdk.getErrorWidget(kind).then((w) => {
            setPlaceholder(w);
          });
          if (onError) onError(e);
        });
    }
  }, [isInView, hasLoadedWidget, sdk, kind, config, variation, themeOverrides, selector, onLoaded, onError, elementId]);

  // Mount widget when selector changes (only if widget exists)
  useEffect(() => {
    if (widget.current) {
      widget.current.mount(selector);
    }
  }, [selector, elementId]);

  return (
    <div ref={containerRef} className={cn(className, 'bg-white rounded-xl overflow-hidden shadow-widget')} id={elementId}>
      <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: placeholder }} />
    </div>
  );
}
